import useToastsStore from '../../../../client/stores/toastsStore.js';
import { apiPostCancel } from '../../../apiClient.js';
import { getCurrentPlayer, getOtherPlayer, isBotGame } from '../../../../shared/app/hostedGameUtils.js';
import { pseudoString } from '../../../../shared/app/pseudoUtils.js';
import { getOpponent, iAmInGame, isMe, viewingGame } from '../../context-utils.js';
import { notifier } from '../notifier.js';
import { t } from 'i18next';
import { IconPersonFillExclamation, IconTablerSwords } from '../../../vue/icons.js';

notifier.on('gameStart', hostedGame => {
    if (isBotGame(hostedGame)) {
        return;
    }

    if (viewingGame(hostedGame)) {
        return;
    }

    if (hostedGame.host === null) {
        // If no host, notify if I am in the game
        if (!iAmInGame(hostedGame)) {
            return;
        }
    } else {
        // Do not notify player who just joined, because he is now aware that game started obviously
        if (!isMe(hostedGame.host)) {
            return;
        }
    }

    const opponent = getOpponent(hostedGame);

    if (opponent === null) {
        return;
    }

    useToastsStore().addToast(
        t('game_with_player_has_started', { player: pseudoString(opponent, 'pseudo') }),
        {
            level: 'success',
            autoCloseAfter: 0,
            closable: false,
            icon: IconPersonFillExclamation,
            actions: [
                { label: t('go_to_the_game'), action: {
                    name: 'online-game',
                    params: { gameId: hostedGame.publicId },
                } },
            ],
        },
    );
});

// Toast when my opponent passed, or, if I'm watching, any player passed
notifier.on('move', (hostedGame, timestampedMove) => {
    if (timestampedMove.move !== 'pass') {
        return;
    }

    const currentPlayer = getCurrentPlayer(hostedGame);

    if (currentPlayer === null) {
        return;
    }

    const passingPlayer = getOtherPlayer(hostedGame, currentPlayer);

    if (passingPlayer === null || isMe(passingPlayer)) {
        return;
    }

    useToastsStore().addToast(
        t('player_passed_his_turn', { player: pseudoString(passingPlayer, 'pseudo') }),
        {
            level: 'warning',
        },
    );
});

notifier.on('takebackRequested', (hostedGame, byPlayer) => {
    // Only display to my opponent and watchers
    if (isMe(byPlayer)) {
        return;
    }

    useToastsStore().addToast(
        t('undo.player_wants_to_takeback', { player: pseudoString(byPlayer, 'pseudo') }),
        {
            level: 'warning',
        },
    );
});

notifier.on('takebackAnswered', (hostedGame, accepted, playerTakeback) => {
    const opponent = getOtherPlayer(hostedGame, playerTakeback);

    // Only display to player who requested takeback and watchers (so: everyone except playerTakeback's opponent)
    if (opponent && isMe(opponent)) {
        return;
    }

    if (accepted) {
        useToastsStore().addToast(
            t('undo.player_takeback_his_move', { player: pseudoString(playerTakeback, 'pseudo') }),
            { level: 'success' },
        );
    } else {
        useToastsStore().addToast(
            t('undo.takeback_request_denied', { player: pseudoString(playerTakeback, 'pseudo') }),
            { level: 'danger' },
        );
    }
});

notifier.on('gameChallengeCreated', hostedGame => {
    if (hostedGame.host === null) {
        return;
    }

    useToastsStore().addToast(
        t('player_challenged_you', { player: pseudoString(hostedGame.host, 'pseudo') }),
        {
            level: 'success',
            autoCloseAfter: 0,
            closable: true,
            icon: IconTablerSwords,
            actions: [
                { label: t('go_to_the_game'), action: {
                    name: 'online-game',
                    params: { gameId: hostedGame.publicId },
                } },
                { label: t('decline_challenge'), classes: 'btn btn-sm btn-outline-warning', action: () => {
                    void apiPostCancel(hostedGame.publicId);
                } },
            ],
        },
    );
});

notifier.on('rematchOffer', hostedGame => {
    const rematchRequester = hostedGame.rematch?.host;

    if (!rematchRequester) {
        return;
    }

    // Displays a toast to opponent
    if (isMe(rematchRequester)) {
        return;
    }

    if (iAmInGame(hostedGame)) {
        useToastsStore().addToast(
            t('player_sent_you_rematch_offer', { player: pseudoString(rematchRequester, 'pseudo') }),
            { level: 'success' },
        );
    } else {
        useToastsStore().addToast(
            t('player_sent_rematch_offer', { player: pseudoString(rematchRequester, 'pseudo') }),
            { level: 'info' },
        );
    }
});
